// Lightweight, request-TEXT heuristic that mirrors the backend's existing bulk
// keyword/date-range cues (assistant.py _ACTION_CUES["bulk"]). Its ONLY job is to
// decide whether to offer the "batches of 50 vs everything together" choice BEFORE
// the AI runs. It never changes the meaning of the request; when uncertain it
// returns false so the normal small-request flow is preserved.
const BULK_CUES = [
  "every day", "each day", "daily", "weekly", "every week", "every month",
  "whole week", "this week", "next week", "next month", "this month",
  "whole month", "entire", "recurring", "repeat", "fill my", "all my",
  "weekday", "weekdays",
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
  // fi
  "joka päivä", "joka viikko", "viikko", "kuukausi", "toistuv", "koko viikko",
  "maanantai", "tiistai", "keskiviikko", "torstai", "perjantai", "lauantai", "sunnuntai",
  // sv
  "varje dag", "varje vecka", "veckan", "månad", "hela veckan",
  "måndag", "tisdag", "onsdag", "torsdag", "fredag", "lördag", "söndag",
];

// Editing action verbs (mirrors backend _ACTION_CUES create/move/edit/delete).
const EDIT_VERBS = [
  "add", "create", "schedule", "book", "set up", "put ", "insert", "plan", "fill",
  "move", "reschedule", "shift", "postpone", "push to",
  "change", "edit", "update", "rename", "extend", "shorten",
  "delete", "remove", "cancel", "clear",
  // fi
  "lisää", "luo", "varaa", "siirrä", "muuta", "päivitä", "poista", "peruuta", "täytä", "suunnittele",
  // sv
  "lägg till", "skapa", "boka", "flytta", "ändra", "uppdatera", "ta bort", "avboka", "fyll", "planera",
];

export function looksLargeRequest(message) {
  const t = (message || "").toLowerCase().trim();
  if (!t) return false;
  // Strong signals stand alone (explicit span or explicit large count).
  if (/\bfor\s+\d+\s+(day|days|week|weeks|month|months|year|years)\b/.test(t)) return true;
  if (/\b\d{2,}\s+(event|events|meeting|meetings|session|sessions|slot|slots|times?)\b/.test(t)) return true;
  // Keyword cues (recurring / bulk / "all my events") ONLY count when paired with
  // an editing verb, so read-only questions ("what do I have this week?") don't
  // trigger the choice. Preserves normal flow when the intent is uncertain.
  const hasCue = BULK_CUES.some((c) => t.includes(c));
  // Also cover "move every workout event", "each of my events", "all of my ..."
  const everyEvents = /\b(every|each|all)\b[\s\S]{0,40}\bevents?\b/.test(t) ||
    /\bevery one of\b/.test(t) || /\beach of my\b/.test(t) || /\ball of my\b/.test(t);
  if (!hasCue && !everyEvents) return false;
  return EDIT_VERBS.some((v) => t.includes(v));
}

// Truthful AI-generation progress math. `generated` = operations the AI has
// actually produced so far; `expected` = declared plan total (may be unknown
// until the first response returns). Returns null batch/pct while expected is
// unknown so the UI can show an honest indeterminate state (never fake %).
export function genBatchInfo(generated, expected, batchSize = 50) {
  const total = expected && expected > 0 ? expected : null;
  const gen = Math.max(0, generated || 0);
  const totalBatches = total ? Math.max(1, Math.ceil(total / batchSize)) : null;
  const batch = total ? Math.min(totalBatches, Math.max(1, Math.ceil(gen / batchSize) || 1)) : null;
  const pct = total ? Math.min(100, Math.round((gen / total) * 100)) : null;
  return { batch, totalBatches, pct, generated: gen, expected: total };
}
